// Real Visitor Analytics Tracking Helper for Himalayan Store
// Logs actual real user visits without fabrication.
// Integrates with Google Analytics 4 (GA4) and server data/analytics.json.

const STORAGE_KEY = 'himalayan_real_visits_log';
const SESSION_KEY = 'himalayan_session_recorded';
const SERVER_STATS_CACHE_KEY = 'himalayan_server_visitor_stats';

const resolveAnalyticsEndpoint = (action = 'record') => {
  if (typeof window === 'undefined') return `/api/analytics.php?action=${action}`;
  const isSubfolder = window.location.pathname.includes('/himalayan');
  const base = isSubfolder ? '/himalayan/api/analytics.php' : '/api/analytics.php';
  return `${window.location.origin}${base}?action=${action}`;
};

export const recordRealVisit = (page = '/') => {
  if (typeof window === 'undefined') return;

  try {
    const todayStr = new Date().toISOString().slice(0, 10);
    const sessionToken = `${todayStr}_${page}`;

    // 1. Google Analytics 4 (GA4) Page View Integration
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'page_view', {
          page_title: document.title || 'ہمالین پنسار سٹور',
          page_location: window.location.href,
          page_path: page || window.location.pathname || '/'
        });
      } catch (gaErr) {
        // Silently continue if GA blocked by adblocker
      }
    }

    // Ensure we count unique visitor session per day per page
    const alreadyRecorded = sessionStorage.getItem(SESSION_KEY);
    if (alreadyRecorded === sessionToken) {
      return;
    }

    sessionStorage.setItem(SESSION_KEY, sessionToken);

    const raw = localStorage.getItem(STORAGE_KEY);
    const logs = raw ? JSON.parse(raw) : [];

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const newVisit = {
      id: `v-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      timestamp: Date.now(),
      date: todayStr,
      time: new Date().toLocaleTimeString('ur-PK', { hour: '2-digit', minute: '2-digit' }),
      page: page || '/',
      device: isMobile ? 'موبائل (Mobile)' : 'کمپیوٹر (Desktop)',
      referrer: document.referrer ? new URL(document.referrer).hostname : 'براہ راست (Direct)'
    };

    // Keep up to 2000 most recent visit records locally
    const updated = [newVisit, ...logs].slice(0, 2000);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    // 2. Dispatch to Server API Handler (safely persists to data/analytics.json)
    try {
      const endpoint = resolveAnalyticsEndpoint('record');
      const payload = JSON.stringify(newVisit);

      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
      } else {
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true
        }).catch(() => {});
      }
    } catch (netErr) {
      // Local fallback already saved
    }

  } catch (err) {
    console.warn('Analytics record error:', err);
  }
};

export const getRealVisitorStats = () => {
  if (typeof window === 'undefined') {
    return {
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      total: 0,
      mobilePercent: 0,
      desktopPercent: 0,
      dailyTrend: [],
      recentVisits: []
    };
  }

  // Check if server stats cache exists and is fresh (within 30 seconds)
  try {
    const cachedServer = localStorage.getItem(SERVER_STATS_CACHE_KEY);
    if (cachedServer) {
      const parsed = JSON.parse(cachedServer);
      if (parsed && parsed.data && (Date.now() - parsed.timestamp < 30000)) {
        return parsed.data;
      }
    }
  } catch (e) {}

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const logs = raw ? JSON.parse(raw) : [];

    const now = Date.now();
    const todayStr = new Date().toISOString().slice(0, 10);
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    let todayCount = 0;
    let weekCount = 0;
    let monthCount = 0;
    let mobileCount = 0;
    let desktopCount = 0;

    const dayBuckets = {};
    // Pre-fill last 7 days
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now - i * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      dayBuckets[d] = 0;
    }

    logs.forEach(visit => {
      const ts = visit.timestamp || new Date(visit.date).getTime();
      const visitDate = visit.date || new Date(ts).toISOString().slice(0, 10);

      if (visitDate === todayStr) todayCount++;
      if (ts >= sevenDaysAgo) weekCount++;
      if (ts >= thirtyDaysAgo) monthCount++;

      if (visit.device?.includes('موبائل') || visit.device?.includes('Mobile')) {
        mobileCount++;
      } else {
        desktopCount++;
      }

      if (dayBuckets[visitDate] !== undefined) {
        dayBuckets[visitDate]++;
      }
    });

    const total = logs.length;
    const mobilePercent = total > 0 ? Math.round((mobileCount / total) * 100) : 0;
    const desktopPercent = total > 0 ? 100 - mobilePercent : 0;

    const dailyTrend = Object.entries(dayBuckets).map(([dateStr, count]) => {
      const parts = dateStr.split('-');
      const shortDate = `${parts[2]}/${parts[1]}`;
      return {
        date: dateStr,
        label: shortDate,
        count
      };
    });

    return {
      today: todayCount,
      thisWeek: weekCount,
      thisMonth: monthCount,
      total,
      mobilePercent,
      desktopPercent,
      dailyTrend,
      recentVisits: logs.slice(0, 15)
    };
  } catch (err) {
    console.warn('Analytics stats error:', err);
    return {
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      total: 0,
      mobilePercent: 0,
      desktopPercent: 0,
      dailyTrend: [],
      recentVisits: []
    };
  }
};

/**
 * Asynchronously fetch real visitor stats from server API (data/analytics.json)
 */
export const fetchServerVisitorStats = async () => {
  if (typeof window === 'undefined') return null;

  try {
    const endpoint = resolveAnalyticsEndpoint('stats');
    const res = await fetch(endpoint, {
      headers: { 'Accept': 'application/json' }
    });

    if (res.ok) {
      const data = await res.json();
      if (data && (data.status === 'success' || typeof data.total === 'number')) {
        // Cache server stats locally
        localStorage.setItem(SERVER_STATS_CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          data
        }));
        return data;
      }
    }
  } catch (err) {
    // Return fallback from local storage
  }

  return getRealVisitorStats();
};
